// app/(todos)/todos/components/TaskList.tsx
'use client';

import { type TodoTask } from '@prisma/client';
import { format, isFuture, isPast, isToday, isTomorrow } from 'date-fns';
import { ru } from 'date-fns/locale';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import { Badge } from '../../../components/ui/badge';
import { deleteTask } from '../lib/actions/todoActions';
import {
  buildWeatherByTaskId,
  getForecast,
  type ForecastDay,
  type WeatherHour,
} from '../lib/forecastClient';

import { TaskDetailsDialog } from './TaskDetailsDialog';
import { TodoItem } from './TodoItem';

// ✅ react-window v2
import { List, type RowComponentProps } from 'react-window';
import { useRouter } from 'next/navigation';

function formatRussianDate(date: Date) {
  const day = format(date, 'd');
  const weekday = format(date, 'EEEE', { locale: ru });

  const monthsGenitive: Record<string, string> = {
    январь: 'января',
    февраль: 'февраля',
    март: 'марта',
    апрель: 'апреля',
    май: 'мая',
    июнь: 'июня',
    июль: 'июля',
    август: 'августа',
    сентябрь: 'сентября',
    октябрь: 'октября',
    ноябрь: 'ноября',
    декабрь: 'декабря',
  };

  const monthNominative = format(date, 'LLLL', { locale: ru }).toLowerCase();
  const monthGenitive = monthsGenitive[monthNominative] || monthNominative;

  const formattedWeekday = weekday.charAt(0).toUpperCase() + weekday.slice(1);
  return `${day} ${monthGenitive}, ${formattedWeekday}`;
}

function getDateBadge(date: Date | null) {
  if (!date) return null;
  if (isToday(date)) return <Badge className="ml-2 bg-emerald-600/80">Сегодня</Badge>;
  if (isTomorrow(date)) return <Badge className="ml-2 bg-sky-600/80">Завтра</Badge>;
  if (isPast(date)) return <Badge className="ml-2 bg-rose-600/80">Просрочено</Badge>;
  return null;
}

interface TaskListProps {
  tasks: TodoTask[];
  activeFilter: string;
  viewTitle: string;
  onTaskDeleted: (taskId: string) => void;
  onTaskUpdated: (updatedTask: TodoTask) => void;
  initialOpenTaskId?: string | null;
}

type Group = { key: string; sortValue: number; tasks: TodoTask[] };
type Row = { type: 'header'; key: string; date: Date | null } | { type: 'task'; task: TodoTask };

const VIRTUALIZE_FROM_ROWS = 220; // можно менять
const ENABLE_ANIMATIONS_UP_TO = 150;

// --- Virtual row (react-window v2) ---
type VirtualRowProps = {
  rows: Row[];
  weatherByTaskId: Map<string, WeatherHour | null>;
  onTaskDeleted: (id: string) => void;
  onTaskUpdated: (t: TodoTask) => void;
  onOpenDetails: (id: string) => void;
  onOpenEdit: (id: string) => void;
};

function VirtualRow({
  index,
  style,
  rows,
  weatherByTaskId,
  onTaskDeleted,
  onTaskUpdated,
  onOpenDetails,
  onOpenEdit,
}: RowComponentProps<VirtualRowProps>) {
  const row = rows[index];

  if (!row) return <div style={style} />;

  if (row.type === 'header') {
    const date = row.date;
    return (
      <div style={style} className="px-1 py-1">
        <div className="text-base md:text-lg font-bold text-white bg-slate-800/80 backdrop-blur-sm rounded-xl shadow-md px-3 py-2 flex items-center gap-2">
          {row.key === 'Без срока' ? 'Без срока' : formatRussianDate(date!)}
          {getDateBadge(date)}
        </div>
      </div>
    );
  }

  const t = row.task;
  return (
    <div style={style} className="px-1 py-1">
      <TodoItem
        task={t}
        weatherHour={weatherByTaskId.get(t.id) ?? null}
        onDeleteOptimistic={onTaskDeleted}
        onUpdate={onTaskUpdated}
        onOpenDetails={onOpenDetails}
        onOpenEdit={onOpenEdit}
      />
    </div>
  );
}

export function TaskList({
  tasks,
  activeFilter,
  onTaskDeleted,
  onTaskUpdated,
  viewTitle,
  initialOpenTaskId,
}: TaskListProps) {
  // --------- один общий диалог ----------
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [startEditing, setStartEditing] = useState(false);

  const selectedTask = useMemo(
    () => (selectedTaskId ? (tasks.find((t) => t.id === selectedTaskId) ?? null) : null),
    [tasks, selectedTaskId],
  );

  const openDetails = useCallback((taskId: string) => {
    setSelectedTaskId(taskId);
    setStartEditing(false);
    setDetailsOpen(true);
  }, []);

  const openEdit = useCallback((taskId: string) => {
    setSelectedTaskId(taskId);
    setStartEditing(true);
    setDetailsOpen(true);
  }, []);

  const router = useRouter();
  const openedFromUrlRef = useRef(false);

  useEffect(() => {
    if (!initialOpenTaskId) return;

    // в dev/StrictMode эффект может сработать дважды — защитимся
    if (openedFromUrlRef.current) return;
    openedFromUrlRef.current = true;

    const t = tasks.find((x) => x.id === initialOpenTaskId);
    if (!t) {
      toast.error('Задача не найдена (возможно, была удалена).');
      router.replace('/todos');
      return;
    }

    setSelectedTaskId(t.id);
    setStartEditing(false);
    setDetailsOpen(true);

    // чтобы при перезагрузке не открывалось снова
    router.replace('/todos');
  }, [initialOpenTaskId, tasks, router]);


  // --------- фильтр + группировка ----------
  const { groupedActive, completedTasks } = useMemo(() => {
    const filtered = tasks.filter((t) => {
      if (activeFilter === 'today') return !!(t.dueDate && isToday(new Date(t.dueDate)));
      if (activeFilter === 'upcoming') return !!(t.dueDate && isFuture(new Date(t.dueDate)));
      if (activeFilter === 'inbox') return !t.projectId;
      if (activeFilter === 'overdue')
        return !!(t.dueDate && isPast(new Date(t.dueDate)) && !isToday(new Date(t.dueDate)));
      if (activeFilter === 'completed') return t.isCompleted;
      return t.projectId === activeFilter;
    });

    const active = activeFilter === 'completed' ? [] : filtered.filter((t) => !t.isCompleted);
    const completed = activeFilter !== 'completed' ? [] : filtered.filter((t) => t.isCompleted);

    const activeSorted = active.slice().sort((a, b) => {
      const at = a.dueDate ? new Date(a.dueDate).getTime() : Number.POSITIVE_INFINITY;
      const bt = b.dueDate ? new Date(b.dueDate).getTime() : Number.POSITIVE_INFINITY;
      return at - bt;
    });

    const completedSorted = completed.slice().sort((a, b) => {
      const at = a.completedAt ? new Date(a.completedAt).getTime() : 0;
      const bt = b.completedAt ? new Date(b.completedAt).getTime() : 0;
      return bt - at;
    });

    const map = new Map<string, Group>();

    for (const task of activeSorted) {
      if (!task.dueDate) {
        const key = 'Без срока';
        const g = map.get(key) ?? { key, sortValue: Number.POSITIVE_INFINITY, tasks: [] };
        g.tasks.push(task);
        map.set(key, g);
        continue;
      }

      const d = new Date(task.dueDate);
      const key = format(d, 'yyyy-MM-dd');
      const sortValue = new Date(key).getTime();

      const g = map.get(key) ?? { key, sortValue, tasks: [] };
      g.tasks.push(task);
      map.set(key, g);
    }

    const groupedActive = Array.from(map.values()).sort((a, b) => a.sortValue - b.sortValue);

    return { groupedActive, completedTasks: completedSorted };
  }, [tasks, activeFilter]);

  const isEmpty = groupedActive.length === 0 && completedTasks.length === 0;

  // --------- погода (один запрос на список) ----------
  const needForecast = useMemo(() => tasks.some((t) => !!t.dueDate), [tasks]);
  const [forecast, setForecast] = useState<ForecastDay[] | null>(null);

  useEffect(() => {
    let alive = true;

    if (!needForecast) {
      setForecast(null);
      return;
    }

    getForecast()
      .then((data) => {
        if (!alive) return;
        setForecast(data);
      })
      .catch((e) => {
        console.error('[TaskList] forecast load failed:', e);
        if (!alive) return;
        setForecast(null);
      });

    return () => {
      alive = false;
    };
  }, [needForecast]);

  const weatherByTaskId = useMemo(() => {
    return buildWeatherByTaskId(
      tasks.map((t) => ({ id: t.id, dueDate: t.dueDate })),
      forecast,
    );
  }, [tasks, forecast]);

  // --------- rows для виртуализации ----------
  const rows: Row[] = useMemo(() => {
    const out: Row[] = [];
    for (const g of groupedActive) {
      out.push({
        type: 'header',
        key: g.key,
        date: g.key === 'Без срока' ? null : new Date(g.key),
      });
      for (const t of g.tasks) out.push({ type: 'task', task: t });
    }
    return out;
  }, [groupedActive]);

  const enableVirtual = rows.length >= VIRTUALIZE_FROM_ROWS;
  const enableAnimations = !enableVirtual && rows.length <= ENABLE_ANIMATIONS_UP_TO;

  // --------- удаление из диалога ----------
  const handleDeleteFromDialog = useCallback(async () => {
    if (!selectedTaskId) return;

    // быстрый UX: убираем из UI сразу
    onTaskDeleted(selectedTaskId);
    setDetailsOpen(false);

    try {
      await toast.promise(deleteTask(selectedTaskId), {
        loading: 'Удаление...',
        success: 'Задача удалена!',
        error: 'Ошибка удаления.',
      });
    } catch {
      // если сервер упал — хотя бы покажем сообщение (и можно сделать router.refresh() если синхронизируешь state)
      toast.error('Не удалось удалить на сервере. Обнови страницу.');
    }
  }, [selectedTaskId, onTaskDeleted]);

  return (
    <section aria-labelledby="task-list-title" className="h-full">
      {isEmpty && (
        <div className="text-center py-10 text-slate-400 flex flex-col items-center gap-4">
          <CheckCircle2 size={48} className="text-emerald-500" />
          <h3 className="font-bold text-lg text-slate-300">{`В разделе "${viewTitle}" всё выполнено!`}</h3>
          <p className="text-sm max-w-xs">Можно отдохнуть или добавить новые задачи.</p>
        </div>
      )}

      {!isEmpty && enableVirtual ? (
        <div className="h-full">
          <List
            defaultHeight={560} // ✅ важно для SSR/первого рендера
            overscanCount={10}
            rowComponent={VirtualRow}
            rowCount={rows.length}
            rowHeight={(index, { rows }) => (rows[index]?.type === 'header' ? 52 : 92)}
            rowProps={{
              rows,
              weatherByTaskId,
              onTaskDeleted,
              onTaskUpdated,
              onOpenDetails: openDetails,
              onOpenEdit: openEdit,
            }}
            style={{ height: '100%' }} // ✅ список занимает всю высоту контейнера
          />
        </div>
      ) : (
        <>
          {groupedActive.map((g) => {
            const date = g.key !== 'Без срока' ? new Date(g.key) : null;

            return (
              <section key={g.key} className="mb-6" aria-labelledby={`date-group-${g.key}`}>
                <h2
                  id={`date-group-${g.key}`}
                  className="text-base md:text-lg font-bold text-white mb-2 sticky top-0 bg-slate-800/80 backdrop-blur-sm rounded-xl shadow-md px-3 py-2 flex items-center gap-2 z-10"
                >
                  {g.key === 'Без срока' ? 'Без срока' : formatRussianDate(date!)}
                  {getDateBadge(date)}
                </h2>

                <ul className="space-y-2">
                  {enableAnimations ? (
                    <AnimatePresence>
                      {g.tasks.map((t) => (
                        <motion.li
                          key={t.id}
                          layout
                          initial={{ opacity: 0, y: -16 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, x: -40 }}
                          transition={{ duration: 0.25 }}
                        >
                          <TodoItem
                            task={t}
                            weatherHour={weatherByTaskId.get(t.id) ?? null}
                            onDeleteOptimistic={onTaskDeleted}
                            onUpdate={onTaskUpdated}
                            onOpenDetails={openDetails}
                            onOpenEdit={openEdit}
                          />
                        </motion.li>
                      ))}
                    </AnimatePresence>
                  ) : (
                    g.tasks.map((t) => (
                      <li key={t.id}>
                        <TodoItem
                          task={t}
                          weatherHour={weatherByTaskId.get(t.id) ?? null}
                          onDeleteOptimistic={onTaskDeleted}
                          onUpdate={onTaskUpdated}
                          onOpenDetails={openDetails}
                          onOpenEdit={openEdit}
                        />
                      </li>
                    ))
                  )}
                </ul>
              </section>
            );
          })}
        </>
      )}

      {/* ✅ один общий диалог */}
      {selectedTask && (
        <TaskDetailsDialog
          task={selectedTask}
          open={detailsOpen}
          onOpenChange={(v) => {
            setDetailsOpen(v);
            if (!v) setStartEditing(false);
          }}
          startEditing={startEditing}
          onStartEditingChange={setStartEditing}
          onDelete={handleDeleteFromDialog}
          onUpdate={(patch) => onTaskUpdated({ ...selectedTask, ...patch })}
        />
      )}

      {/* выполненные (как было) */}
      {completedTasks.length > 0 && activeFilter === 'completed' && (
        <section className="mt-8" aria-labelledby="completed-tasks-title">
          <h2 id="completed-tasks-title" className="text-lg font-bold text-white mb-2">
            Выполненные задачи
          </h2>
          <ul className="space-y-2">
            {completedTasks.map((t) => (
              <li key={t.id}>
                <TodoItem
                  task={t}
                  weatherHour={weatherByTaskId.get(t.id) ?? null}
                  onDeleteOptimistic={onTaskDeleted}
                  onUpdate={onTaskUpdated}
                  onOpenDetails={openDetails}
                  onOpenEdit={openEdit}
                />
              </li>
            ))}
          </ul>
        </section>
      )}
    </section>
  );
}
